import { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, BorderStyle, WidthType, AlignmentType, HeadingLevel } from 'docx';
import { saveAs } from 'file-saver';
import { calculateTotalGSTBreakdown } from './gstUtils';
import { roundForDisplay } from './mathUtils';

export const generateDOCX = async (data, type = 'estimate', companySettings = null, options = {}) => {
  // Use default company settings if not provided
  if (!companySettings) {
    companySettings = {
      companyName: 'IDEAL TRENDZ INTERIORS',
      addressLine1: '# Vertex Privilege, Villa No. 32, Nizampet Road',
      addressLine2: '',
      city: 'HYDERNAGAR',
      state: 'TELANGANA',
      pincode: '500072',
      contactNumber: '9014814901',
      emailId: 'idealtrendz.int@gmail.com',
      gstNumber: '36AHZPN3524A1Z9',
      panNumber: 'AHZPN3524A',
      bankName: 'Not Configured',
      accountHolderName: 'Not Configured',
      accountNumber: 'Not Configured',
      ifscCode: 'Not Configured',
      branchName: 'Not Configured'
    };
  }

  const {
    items = [],
    customer_name = '',
    customer_address = '',
    customer_city = '',
    customer_gst = '',
    estimate_number = '',
    invoice_number = '',
    date = new Date().toISOString(),
    isWoodWork = false,
    discount = 0
  } = data;

  const documentNumber = type === 'invoice' ? invoice_number : estimate_number;
  const documentTitle = type === 'invoice' ? 'TAX INVOICE' : 'ESTIMATE';

  // Calculate GST Breakdown
  const effectiveItems = items.map(item => ({
    ...item,
    quantity: item.quantity || 1,
    unit_price: item.unit_price || 0,
    gst_rate: item.gst_rate || 0,
    discount: item.discount || discount || 0
  }));

  const gstBreakdown = calculateTotalGSTBreakdown(effectiveItems, customer_gst, companySettings);

  const createCell = (text, opts = {}) => {
    return new TableCell({
      children: [new Paragraph({ children: [new TextRun({ text: String(text), bold: opts.bold, size: opts.size || 20 })], alignment: opts.alignment || AlignmentType.LEFT })],
      margins: { top: 100, bottom: 100, left: 100, right: 100 },
      ...opts.cellOptions
    });
  };

  const headerRows = [
    new TableRow({
      children: [
        createCell('S.No', { bold: true, alignment: AlignmentType.CENTER, cellOptions: { width: { size: 5, type: WidthType.PERCENTAGE } } }),
        createCell('Description', { bold: true, cellOptions: { width: { size: 45, type: WidthType.PERCENTAGE } } }),
        createCell('Qty', { bold: true, alignment: AlignmentType.CENTER }),
        createCell('Unit', { bold: true, alignment: AlignmentType.CENTER }),
        createCell('Price', { bold: true, alignment: AlignmentType.RIGHT }),
        createCell('Total', { bold: true, alignment: AlignmentType.RIGHT })
      ]
    })
  ];

  let currentCategory = '';
  let sNo = 1;

  effectiveItems.forEach((item) => {
    // Add category header if it changes (and not 'Others')
    const itemCategory = item.category_name || item.categoryLabel || item.template_category || 'Others';
    if (itemCategory !== currentCategory && itemCategory.toLowerCase() !== 'others') {
      currentCategory = itemCategory;
      headerRows.push(
        new TableRow({
          children: [
            new TableCell({
              children: [new Paragraph({ children: [new TextRun({ text: currentCategory, bold: true })] })],
              columnSpan: 6,
              margins: { top: 100, bottom: 100, left: 100, right: 100 }
            })
          ]
        })
      );
    }

    const description = item.product_name || item.name || '';
    const quantity = roundForDisplay(item.quantity);
    const unit = item.unit_type || '';
    const price = roundForDisplay(item.unit_price);
    
    const taxableAmount = (item.quantity * item.unit_price) * (1 - (item.discount || 0) / 100);
    const totalStr = roundForDisplay(taxableAmount);

    headerRows.push(
      new TableRow({
        children: [
          createCell(sNo++, { alignment: AlignmentType.CENTER }),
          createCell(description),
          createCell(quantity, { alignment: AlignmentType.CENTER }),
          createCell(unit, { alignment: AlignmentType.CENTER }),
          createCell(price, { alignment: AlignmentType.RIGHT }),
          createCell(totalStr, { alignment: AlignmentType.RIGHT })
        ]
      })
    );
  });

  const doc = new Document({
    sections: [
      {
        properties: {},
        children: [
          new Paragraph({
            text: documentTitle,
            heading: HeadingLevel.HEADING_1,
            alignment: AlignmentType.CENTER
          }),
          new Paragraph({
            children: [
              new TextRun({ text: companySettings.companyName, bold: true, size: 28 }),
              new TextRun({ text: `\n${companySettings.addressLine1} ${companySettings.addressLine2}`, break: 1 }),
              new TextRun({ text: `\n${companySettings.city}, ${companySettings.state} - ${companySettings.pincode}`, break: 1 }),
              new TextRun({ text: `\nPhone: ${companySettings.contactNumber} | Email: ${companySettings.emailId}`, break: 1 }),
              new TextRun({ text: `\nGSTIN: ${companySettings.gstNumber}`, break: 1 })
            ],
            alignment: AlignmentType.CENTER,
            spacing: { after: 200 }
          }),
          new Paragraph({
            children: [
              new TextRun({ text: 'Bill To:', bold: true }),
              new TextRun({ text: `\nName: ${customer_name}`, break: 1 }),
              new TextRun({ text: `\nAddress: ${customer_address} ${customer_city}`, break: 1 }),
              new TextRun({ text: `\nGSTIN: ${customer_gst}`, break: 1 })
            ],
            spacing: { after: 200 }
          }),
          new Paragraph({
            children: [
              new TextRun({ text: `Document No: ${documentNumber}`, bold: true }),
              new TextRun({ text: `\nDate: ${new Date(date).toLocaleDateString()}`, break: 1 })
            ],
            alignment: AlignmentType.RIGHT,
            spacing: { after: 400 }
          }),
          new Table({
            rows: headerRows,
            width: { size: 100, type: WidthType.PERCENTAGE }
          }),
          new Paragraph({ text: '', spacing: { after: 200 } }),
          new Paragraph({
            children: [
              new TextRun({ text: `Subtotal: ₹${roundForDisplay(gstBreakdown.totalTaxableAmount)}`, bold: true }),
              new TextRun({ text: `\nTotal GST: ₹${roundForDisplay(gstBreakdown.totalGSTAmount)}`, break: 1, bold: true }),
              new TextRun({ text: `\nGrand Total: ₹${roundForDisplay(gstBreakdown.grandTotal)}`, break: 1, bold: true, size: 28 })
            ],
            alignment: AlignmentType.RIGHT,
            spacing: { before: 200, after: 400 }
          }),
          new Paragraph({
            children: [
              new TextRun({ text: 'Terms & Conditions:', bold: true }),
              new TextRun({ text: '\n1. All payments are to be made in favor of IDEAL TRENDZ INTERIORS.', break: 1 })
            ]
          })
        ]
      }
    ]
  });

  const blob = await Packer.toBlob(doc);
  saveAs(blob, `${documentTitle}_${documentNumber}.docx`);
};
